import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Col, Row } from 'antd';
import moment from 'moment';
import { AtsSkeleton, Button, BUTTON_TYPES, File, Icon, RichText, Tag } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { getCandidateLoading } from 'store/selectors/commonSelectors';
import './style.scss';
import type { CandidateData } from 'store/reducers/candidateReducer';
import { candidateDocumentToFileItem } from 'helpers/candidateDocument';

interface ExperienceTabContentProps {
  candidateData: CandidateData;
}

export const ExperienceTabContent: React.FC<ExperienceTabContentProps> = ({ candidateData }) => {
  const { t } = useTranslation();
  const loading = useSelector(getCandidateLoading);

  const [specialitiesOpen, setSpecialitiesOpen] = useState(false);

  const onChangeSpecialitiesOpen = () => setSpecialitiesOpen(o => !o);

  if (loading)
    return (
      <div className="job-experience__loading">
        <AtsSkeleton count={3} height={145} />
      </div>
    );

  return (
    <div className="job-experience">
      <div className="job-experience__specialities">
        <Row gutter={[32, 8]}>
          {candidateData.specialities.map((s, index) =>
            !index ? (
              <Col key={s.uuid + index} span={12} lg={12} md={12} sm={24}>
                <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
                  {s.level} {s.speciality}
                </Typography.Title3>
              </Col>
            ) : (
              specialitiesOpen && (
                <Col key={s.uuid + index} span={12} lg={12} md={12} sm={24}>
                  <Typography.Text>
                    {s.level} {s.speciality}
                  </Typography.Text>
                </Col>
              )
            )
          )}
        </Row>
        {candidateData.specialities.length > 1 && (
          <Button type={BUTTON_TYPES.TEXT} onClick={onChangeSpecialitiesOpen}>
            {specialitiesOpen
              ? t('translation:experience_tab_content.button_show_less_specialties')
              : t('translation:experience_tab_content.button_show_more_specialties')}
          </Button>
        )}
      </div>
      <Row gutter={[32, 0]}>
        <Col span={12}>
          <div className="job-experience__block">
            <Typography.Label className="job-experience__label">
              {t('translation:experience_tab_content.label_sources')}
            </Typography.Label>
            {candidateData.sources.map(s => (
              <div key={s.source}>
                {s.additional_source_name ? (
                  <>
                    <span style={{ color: '#717C8C' }}>
                      {String(s.source).charAt(0).toUpperCase() + String(s.source).slice(1)}:{' '}
                    </span>
                    {s.additional_source_name}
                  </>
                ) : (
                  s.source
                )}
                {candidateData.sources.length > 1 && s.is_main && (
                  <strong> {t('translation:experience_tab_content.text_main')}</strong>
                )}
              </div>
            ))}
          </div>
          <div className="job-experience__block">
            <Typography.Label className="job-experience__label">
              {t('translation:experience_tab_content.label_languages')}
            </Typography.Label>
            {candidateData.foreign_languages?.length ? (
              candidateData.foreign_languages.map(lang => (
                <div key={lang.language + lang.level}>{`${lang.language}: ${lang.level}`}</div>
              ))
            ) : (
              <Typography.Text>-</Typography.Text>
            )}
          </div>
          <div className="job-experience__sub-block">
            <Typography.Label className="job-experience__label">
              {t('translation:experience_tab_content.label_documents')}
            </Typography.Label>
            {candidateData.cvs?.length ? (
              candidateData.cvs.map(cv => {
                const file = candidateDocumentToFileItem(cv);
                return file && <File key={file.uuid} file={file} />;
              })
            ) : (
              <Typography.Text>-</Typography.Text>
            )}
          </div>
          <div className="job-experience__block">
            <Typography.Label className="job-experience__label">
              {t('translation:experience_tab_content.label_cv_links')}
            </Typography.Label>
            {candidateData.cvs_links?.length ? (
              candidateData.cvs_links.map((link, id) => (
                <div key={link + id} className="cv-link">
                  <Icon type="Link" className="cv-link__icon" />
                  <a className="cv-link__text" href={link} target="_blank" rel="noopener noreferrer">
                    {link}
                  </a>
                </div>
              ))
            ) : (
              <Typography.Text>-</Typography.Text>
            )}
          </div>
          <Typography.Label className="job-experience__label">
            {t('translation:experience_tab_content.label_tags')}
          </Typography.Label>
          {candidateData.tags?.length ? (
            candidateData.tags.map(tag => <Tag key={tag}>{tag}</Tag>)
          ) : (
            <Typography.Text>-</Typography.Text>
          )}
        </Col>
        <Col span={12}>
          <Typography.Label className="job-experience__label">
            {t('translation:experience_tab_content.label_working_experience')}
          </Typography.Label>
          {candidateData.experience.length ? (
            candidateData.experience.map((exp, idx) => (
              <div
                key={exp.position + idx}
                className={cn({ 'job-experience__sub-block': idx !== candidateData.experience.length - 1 })}
              >
                <Typography.Title4>{exp.company}</Typography.Title4>
                <Typography.Caption>
                  {`${
                    exp.worked_from
                      ? moment(exp.worked_from).format('MM.YYYY')
                      : t('translation:experience_tab_content.text_no_date')
                  } - ${
                    exp.is_current
                      ? t('translation:experience_tab_content.text_current')
                      : exp.worked_till
                      ? moment(exp.worked_till).format('MM.YYYY')
                      : t('translation:experience_tab_content.text_no_date')
                  } | ${exp.position}`}
                </Typography.Caption>
                <RichText text={exp.additional_info ?? ''} />
              </div>
            ))
          ) : (
            <Typography.Text>-</Typography.Text>
          )}
        </Col>
      </Row>
    </div>
  );
};
